import axios from "axios";
import Logger from "./logger";
import Md5Service from "./md5Service";
import MergeService from "./mergeService";
import { EventEmitter } from "eventemitter3";
import PrepareService from "./prepareService";
import UploadFileService from "./uploadFileService";
import AbortUploadService from "./abortUploadService";
import CommitBlocksService from "./commitBlocksService"; 
import { calcBlocks, WorkerStatus, ErrorCodes } from "./utils";

export default class Worker extends EventEmitter {
  constructor(options) {
    super();
    this.ms = null; 
    this.tid = null;
    this.source = null;
    this.options = options;
    this.status = WorkerStatus.IDLE;
    this._logger = options.logger || new Logger({ debug: options.debug });
  }

  async startHash(task, fileItem) {
    const { tid } = task;
    const { fid, file } = fileItem;
    const fileName = file.name;
    try {
      this.emit("BeginHash", tid, fid);
      const payload = { tid, fid, fileName };
      const { cancel, data } = await this._calcHash(file, payload);
      this.emit("EndHash", tid, fid);
      if (cancel) {
        return {
          message: "用户取消了计算文件 MD5",
          code: ErrorCodes.USER_ABORT,
        };
      } // 上传被取消了
      return {
        data: data,
        code: ErrorCodes.OK,
      };
    } catch (err) {
      return {
        message: err.message,
        code: ErrorCodes.CALC_MD5_FAILED,
      };
    }
  }

  async startPrepare(task, fileItem, token) {
    const { tid, fid, file, md5 } = fileItem;
    const { pid, size, behavior } = task;
    try {
      this.emit("BeginPrepare", tid, fid);
      const result = await this._prepare(pid, behavior, file, md5, token);
      const { data, cancel } = result;
      if (cancel) {
        return {
          message: "用户取消了初始化上传",
          code: ErrorCodes.USER_ABORT,
        };
      }
      // 通知初始化已完成
      this.emit("EndPrepare", tid, fid);
      return {
        data: data,
        code: ErrorCodes.OK,
      };
    } catch (err) {
      return {
        message: err.message,
        code: ErrorCodes.PREPARE_UPLOAD_FAILED,
      };
    }
  }

  // 对上传的文件预分块
  async startBlocks(task, fileItem, token) {
    const { tid, fid, file, fileId, uploadId, blockSize } = fileItem;
    const fileName = file.name;
    const prefix = `[${tid}-${fileName}]`;
    try {
      this.emit("BeginCommitBlocks", tid, fid);
      const blocks = calcBlocks(blockSize, file.size);
      const result = await this._commit(blocks, fileId, uploadId, token);
      if (result.cancel) {
        return {
          code: ErrorCodes.USER_ABORT,
          message: "用户取消了分块上传",
        };
      }
      this.emit("EndCommitBlocks", tid, fid);
      // 计算已经提交后的大小
      const commitSize = file.size - result.size;
      if (commitSize > 0) { 
        // this._ups.commit(tid, commitSize, result.size);
        this.emit("UploadCommit", { tid, bytes: commitSize, total: result.size });
        this._logger.log(`${prefix} 已上传 ${commitSize} 字节`);
      }
      const count = blocks.length;
      const realCount = result.parts.length;
      this._logger.log(
        `${prefix} 文件被分了 ${count} 块, 需上传 ${realCount} 块`
      );
      return {
        data: result.parts,
        code: ErrorCodes.OK,
      };
    } catch (err) {
      return {
        message: err.message,
        code: ErrorCodes.COMMIT_BLOCKS_FAILED,
      };
    }
  }

  async startUpload(blocks, task, fileItem, token) {
    const { tid, fid, file } = fileItem;
    try {
      this.emit("BeginUploadFile", tid, fid);
      const { cancel } = await this._upload(blocks, task, fileItem, token);
      if (cancel) {
        return {
          code: ErrorCodes.USER_ABORT,
          message: "用户取消了上传文件",
        };
      }
      this.emit("EndUploadFile", tid, fid);
      return {
        code: ErrorCodes.OK,
      };
    } catch (err) {
      return {
        message: err.message,
        code: ErrorCodes.UPLOAD_FILE_FAILED,
      };
    }
  }

  async startMerge(fileItem, token) {
    const { tid, fid } = fileItem;
    const { fileId, uploadId, file, blockNum } = fileItem;
    try {
      this.emit("BeginMergeFile", tid, fid);
      const e = await this._merge(fileId, uploadId, blockNum, token);
      const { cancel, data } = e;
      if (cancel) {
        return {
          code: ErrorCodes.USER_ABORT,
          message: "用户取消了合并文件",
        };
      }
      this.emit("EndMergeFile", tid, fid);
      return {
        code: ErrorCodes.OK,
        data: data,
      };
    } catch (err) {
      return {
        message: err.message,
        code: ErrorCodes.MERGE_FILE_FAILED,
      };
    }
  }

  async abortUploadFile(fileId, uploadId) {
    const headers = this.options.headers;
    const url = this.options.urls.abort;
    const ds = new AbortUploadService(url, headers);
    return await ds.execute(fileId, uploadId);
  }

  _changeStatus(status) {
    if (this.status === status) return;
    this._logger.log(
      `【${this.tid}】工作状态变更: ${this.status} -> ${status}`
    );
    this.status = status;
    this.emit("StatusChange", status);
  }

  /**
   *
   * @param {Object} e <Task, FileItem>
   * { tid, pid, name, isdir, size, status, behavior }
   * { fid, tid, status, md5, uploadId, file, metadatas }
   */
  async start({ task, fileItem }) {
    const { tid, fid, file } = fileItem;
    const { pid, size, behavior } = task;
    this.tid = tid;
    const fileName = file.name;
    try {
      const prefix = `Task: [${tid} - ${fileName}]`;
      this._logger.log(`${prefix} 开始处理，共计 5 步`);
      this._changeStatus(WorkerStatus.BUSY);  
      // 计算文件 md5
      this._logger.log(`${prefix} 1/5 计算文件 MD5`);
      if (!fileItem.md5) {
        const hashData = await this.startHash(task, fileItem);
        switch (hashData.code) {
          case ErrorCodes.USER_ABORT: {
            this.emit("TaskAbort", tid); // 计算 md5 被取消了
            return;
          }
          case ErrorCodes.OK: {
            fileItem.md5 = hashData.data;
            this._logger.log(`${prefix} 计算 MD5 结果 ${hashData.data}`);
            break;
          }
          default: {
            const code = hashData.code;
            const message = hashData.message;
            this._logger.error(`${prefix} 计算 MD5 异常 ${message}`);
            this.emit("error", { tid, fid, code, message });
            return;
          }
        }
      } else {
        this._logger.log(`${prefix} MD5 已存在 ${fileItem.md5}，跳过`);
      }
      this.source = axios.CancelToken.source();
      const token = this.source.token;
      // 初始化上传
      this._logger.log(`${prefix} 2/5 上传初始化`);
      if (!fileItem.uploadId) {
        const prepare = await this.startPrepare(task, fileItem, token);
        switch (prepare.code) {
          case ErrorCodes.USER_ABORT: {
            this.emit("TaskAbort", tid); // 初始化上传被取消了
            return;
          }
          case ErrorCodes.OK: {
            // 初始化完毕，将信息补全
            const { uploadId, files, blockInfo, fastUpload } = prepare.data; 
            const args = {
              metadatas: files,
              md5: fileItem.md5,
              uploadId: uploadId,  
              blockInfo: blockInfo,
            }
            this.emit("Prepared", tid, fid, args); 
            if (fastUpload) {
              this._logger.log(`${prefix} 发生了秒传`);
              const fileSize = fileItem.file.size;
              this.emit("UploadCommit", { tid, bytes: fileSize, total: size });
              this.emit("FileUpload", tid, fid);
              return;
            } 
            const fileId = files[files.length - 1].fileId;
            const { blockNum, blockSize } = blockInfo;
            fileItem.fileId = fileId;
            fileItem.uploadId = uploadId;
            fileItem.metadatas = files;
            fileItem.blockNum = blockNum;
            fileItem.blockSize = blockSize;
            this._logger.log(`${prefix} 初始化成功 ${uploadId}`);
            break;
          }
          default: {
            const code = prepare.code;
            const message = prepare.message;
            this._logger.error(`${prefix} 初始化上传异常 ${message}`);
            this.emit("error", { tid, fid, code, message });
            return;
          }
        }
      } else {
        this._logger.log(`${prefix} 已初始化 ${fileItem.uploadId}, 跳过`);
      }
      // 对上传的文件预分块
      this._logger.log(`${prefix} 3/5 文件分块`);
      const block = await this.startBlocks(task, fileItem, token);
      let uploadBlocks = [];
      switch (block.code) {
        case ErrorCodes.USER_ABORT: {
          this.emit("TaskAbort", tid);
          return;
        }
        case ErrorCodes.OK: {
          uploadBlocks = block.data;
          break;
        }
        default: {
          const code = block.code;
          const message = block.message;
          this._logger.error(`${prefix} 分块异常 ${message}`);
          this.emit("error", { tid, fid, code, message });
          return;
        }
      }
      this._logger.log(`${prefix} 4/5 开始上传`);
      const ud = await this.startUpload(uploadBlocks, task, fileItem, token);
      switch (ud.code) {
        case ErrorCodes.OK: {
          break;
        }
        case ErrorCodes.USER_ABORT: {
          this.emit("TaskAbort", tid);
          return;
        }
        default: {
          const code = ud.code;
          const message = ud.message;
          this._logger.error(`${prefix} 上传异常 ${message}`);
          this.emit("error", { tid, fid, code, message });
          return;
        }
      }
      // 上传完成
      this._logger.log(`${prefix} 5/5 合并文件`);
      const mergeData = await this.startMerge(fileItem, token);
      switch (mergeData.code) {
        case ErrorCodes.OK: {
          this.emit("FileUpload", tid, fid);
          break;
        }
        case ErrorCodes.USER_ABORT: {
          this.emit("TaskAbort", tid);
          return;
        }
        default: {
          const code = mergeData.code;
          const message = mergeData.message;
          this._logger.error(`${prefix} 合并文件异常 ${message}`);
          this.emit("error", { tid, fid, code, message });
          return;
        }
      }
    } catch (err) {
      const code = ErrorCodes.UNKNOWN;
      const message = err.message;
      this._logger.error(`发生严重错误 ${message}`);
      this.emit("error", { tid, fid, code, message });
    } finally {
      this._changeStatus(WorkerStatus.IDLE);
    }
  }

  async stop() {
    if (this.ms) {
      this._logger.log("用户终止计算文件 md5");
      this.ms.abort();
    }
    if (this.source) {
      this._logger.log("用户终止接口的网络请求");
      this.source.cancel();
    }
  }

  _calcHash(file, payload) {
    return new Promise((resolve, reject) => {
      if (this.ms == null) {
        this.ms = new Md5Service();
        this.ms.on("error", (err) => {
          this.ms = null;
          const error = new Error(`计算文件 md5 失败: ${err.message}`);
          reject(error);
        });
        this.ms.on("abort", () => {
          this.ms = null;
          resolve({ cancel: true });
        });
        this.ms.on("progress", (percent) => {
          this.emit("Hashing", { ...payload, percent });
        });
        this.ms.on("complete", (data) => {
          this.ms = null;
          resolve({ data, cancel: false });
        });
      }
      this.ms.start(file);
    });
  }

  _prepare(pid, behavior, file, md5, token) {
    return new Promise((resolve, reject) => {
      const size = file.size;
      const name = file.name;
      let pathList = file.webkitRelativePath.split("/");
      if (pathList.length > 0) {
        pathList = pathList.slice(0, pathList.length - 1);
      }
      pathList = pathList || []
      const headers = this.options.headers;
      const url = this.options.urls.prepare;
      const ps = new PrepareService(url, headers);
      ps.on("error", (err) => reject(err));
      ps.on("abort", () => resolve({ cancel: true }));
      ps.on("complete", (data) => {
        resolve({ data, cancel: false });
      });
      ps.execute(name, pathList, pid, md5, size, behavior, token);
    });
  }

  _commit(blocks, fileId, uploadId, cancelToken) {
    return new Promise((resolve, reject) => {
      const headers = this.options.headers;
      const url = this.options.urls.blocks;
      const cs = new CommitBlocksService(url, headers);
      cs.on("error", (err) => {
        const error = new Error(`提交分片失败: ${err.message}`);
        reject(error);
      });
      cs.on("abort", () => {
        resolve({ cancel: true });
      });
      cs.on("complete", (parts, size) => {
        resolve({ size, parts, cancel: false });
      });
      cs.execute(blocks, fileId, uploadId, cancelToken);
    });
  }

  _upload(blocks, task, fileItem, token) {
    return new Promise(async (resolve, reject) => {
      const headers = this.options.headers;
      const url = this.options.urls.upload;
      const us = new UploadFileService(url, headers);
      us.on("error", (err) => reject(err));
      us.on("abort", () => resolve({ cancel: true }));
      us.on("complete", () => resolve({ cancel: false }));
      us.on("uploadBytes", ({ tid, bytes }) => { 
        this.emit("UploadBytes", { tid, bytes, total: task.size });
      });
      const { tid, fid } = fileItem;
      const { file, fileId, uploadId, blockSize } = fileItem;
      await us.execute(
        tid,
        fid,
        uploadId,
        fileId,
        file,
        blocks,
        blockSize,
        token
      );
    });
  }

  _merge(fileId, uploadId, blockNum, cancelToken) {
    return new Promise((resolve, reject) => {
      const headers = this.options.headers;
      const url = this.options.urls.finish;
      const mes = new MergeService(url, headers);
      mes.on("error", (err) => reject(err));
      mes.on("abort", () => {
        return resolve({ cancel: true });
      });
      mes.on("complete", (data) => {
        return resolve({ cancel: false, data });
      });
      mes.execute(fileId, uploadId, blockNum, cancelToken);
    });
  } 
}
